import dayjs from 'dayjs';
import { decamelize } from 'humps';
import {
  BankingMetrics,
  ConnectedAccount,
  ConnectedAccountsResponse,
  LowActivityAccount,
  LowActivityImportResult,
  LowActivityStatus,
  DebitsBlockStatus,
  FinancialTransactionsResponse,
  CardPurchaseDetail,
  ConversionDetail,
  DepositDetail,
  PaymentDetail,
  TransactionDetail,
  RecentTransactionsResponse,
  GlobalAccount,
  Recipient,
  Rate,
  BankingCurrency,
  BackendRate,
  CreateCardPayload,
  CreateCardholderPayload,
  Cardholder,
  DeleteRecipientResponse,
  GlobalAccountsResponse,
  GlobalAccountCreationOption,
  CreateGlobalAccountResponse,
  CreateGlobalAccountRequest,
  UpdateCardPayload,
  CardFormFactorType,
  BankingDetails,
  PendingRfi,
  TransactionsCountResponse,
  TransactionTypeEnum,
  TransferFeesFilters,
  TransferFeesResponse,
  CreateTransferPayload,
  Card,
  CardDetails,
  CardStatus,
  AirwallexCardDetails,
  TransferConfig,
  CreateTransferConfigPayload,
  UpdateTransferConfigPayload,
  TransferRequest,
  BankingPageTransferRequestsFilters,
  SubmitPaymentFromTransferRequestPayload,
  ConversionsCountResponse,
  RfisCountResponse,
  PaymentsCountResponse,
  BatchFeesRequestItem,
  BatchFeesResponse,
  BatchQuoteResponse,
  BatchSubmitResponse,
  BatchTransfersResponse,
  BankingPageBatchTransfersFilters,
  BatchTransferDetail,
  ChargeDetail,
  RecurringPayment,
  RecurringPaymentsResponse,
  RecurringPaymentExecution,
  CreateRecurringPaymentPayload,
  UpdateRecurringPaymentPayload,
} from '../types/banking';
import { ReportingState } from '../viewsv2/Banking/ConnectedAccountDetail/Reporting';
import { camelToSnake } from '../utils/common';
import { Nullable } from '../types';
import axios from './axios';
import { PaginatedResponse } from './types';

export function getBankingRequests(companyId: number) {
  return axios.get(`companies/${companyId}/products/banking/request/`).then(({ data }) => data);
}

export function submitBankingRequest(companyId: number) {
  return axios.post(`companies/${companyId}/products/banking/request/`).then(({ data }) => data);
}

export function adminGetConnectedAccounts(page: number, pageSize: number, filters: any) {
  const params = new URLSearchParams();
  params.append('page', `${page}`);
  params.append('page_size', `${pageSize}`);
  filters &&
    Object.keys(filters).forEach((key) => {
      params.append(key, filters[key]);
    });
  return axios
    .get<ConnectedAccountsResponse>(`admins/products/banking/accounts/?${params.toString()}`)
    .then(({ data }) => data);
}

export function adminGetConnectedAccountDetails(id: string) {
  return axios.get<ConnectedAccount>(`admins/products/banking/accounts/${id}/`).then(({ data }) => data);
}

export function adminDismissRFIs(id: string) {
  return axios
    .patch<ConnectedAccount>(`admins/products/banking/accounts/${id}/close_rfis/`)
    .then(({ data }) => data);
}

export function adminGetAccountRfis(
  accountId: number,
  { page, pageSize, status, rfiType }: GetRfisParams = {}
): Promise<RfisCountResponse> {
  const params = new URLSearchParams();
  if (page) params.append('page', String(page));
  if (pageSize) params.append('page_size', String(pageSize));
  if (status?.length) params.append('status', status.join(','));
  if (rfiType?.length) params.append('rfi_type', rfiType.join(','));
  return axios
    .get<RfisCountResponse>(`admins/products/banking/accounts/${accountId}/rfis/?${params.toString()}`)
    .then(({ data }) => data);
}

export function adminGetAccountRfiDetail(accountId: number, rfiPk: number): Promise<PendingRfi> {
  return axios.get(`admins/products/banking/accounts/${accountId}/rfis/${rfiPk}/`).then(({ data }) => data);
}

export function getAllAccountsRfis(
  pageSize: number = 10,
  filters: { page: number; status?: string[]; rfiType?: string[]; search?: string }
): Promise<RfisCountResponse> {
  const params = new URLSearchParams();
  if (filters.page) params.append('page', String(filters.page));
  params.append('page_size', String(pageSize));
  if (filters.status?.length) params.append('status', filters.status.join(','));
  if (filters.rfiType?.length) params.append('rfi_type', filters.rfiType.join(','));
  if (filters.search) params.append('search', filters.search);
  return axios
    .get<RfisCountResponse>(`admins/products/banking/rfis/?${params.toString()}`)
    .then(({ data }) => data);
}

export function adminGetFinancialTransactions(accountId: number, pageSize = 500) {
  return axios
    .get<FinancialTransactionsResponse>(
      `admins/products/banking/accounts/${accountId}/financial_transactions/?page_size=${pageSize}`
    )
    .then(({ data }) => data);
}

export function adminGetMappedTransactions(
  accountId: number,
  page: number,
  pageSize: number,
  filters: Record<string, string | undefined>
) {
  const params = Object.entries(filters).reduce((acc, [key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      acc.append(camelToSnake(key), value);
    }
    return acc;
  }, new URLSearchParams());
  params.append('page', `${page}`);
  params.append('page_size', `${pageSize}`);
  return axios
    .get<TransactionsCountResponse>(
      `admins/products/banking/accounts/${accountId}/transactions/?${params.toString()}`
    )
    .then(({ data }) => data);
}

export function adminActivateAccount(id: string) {
  return axios.post(`admins/products/banking/accounts/dev/activate/${id}/`).then(({ data }) => data);
}

export function adminCreateConnectedAccount(payload: {
  companyEmail: string;
  nickname: string;
  companyName: string;
}) {
  return axios.post(`admins/products/banking/accounts/dev/create/`, payload).then(({ data }) => data);
}

export function adminGetBankingMetrics() {
  return axios.get<BankingMetrics>(`admins/products/banking/accounts/aggregations/`).then(
    ({ data }) =>
      ({
        ...data,
        // awful hack to make the keys uppercase because interceptors break all caps keys
        accountsByStatus: Object.fromEntries(
          Object.entries(data.accountsByStatus).map(([key, value]) => [key.toUpperCase(), value || 0])
        ),
      }) as BankingMetrics
  );
}

export function adminGetRecentTransactions(page: number, pageSize: number) {
  return axios
    .get<RecentTransactionsResponse>(`admins/products/banking/transactions/`, {
      params: { page: page.toString(), page_size: pageSize.toString() },
    })
    .then(({ data }) => data);
}

export function adminCreateKycFlow(id: string) {
  return axios
    .post<{
      authorizationData: { url: string };
    }>(`admins/products/banking/accounts/dev/create_hosted_flow/${id}/`)
    .then(({ data }) => data);
}

export function getBankingDetails(companyId: number, accountId: number): Promise<BankingDetails> {
  return axios
    .get<BankingDetails>(`/companies/${companyId}/products/banking/accounts/${accountId}/banking_info/`)
    .then(({ data }) => data);
}

export async function getTransactions(
  companyId: number,
  accountId: number,
  params: Record<string, any>,
  pageNum = 0,
  pageSize = 10
) {
  const queryParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      const snakeKey = camelToSnake(key); // Convert keys to snake_case
      if (Array.isArray(value)) {
        value.forEach((item) => queryParams.append(snakeKey, item));
      } else {
        queryParams.append(snakeKey, value.toString());
      }
    }
  });
  queryParams.append('page', pageNum.toString());
  queryParams.append('page_size', pageSize.toString());

  return axios
    .get<TransactionsCountResponse>(
      `/companies/${companyId}/products/banking/accounts/${accountId}/banking_info/transactions/?${queryParams.toString()}`
    )
    .then(({ data }) => data);
}

export function getRecentTransactions(companyId: number, accountId: number) {
  const params = new URLSearchParams();
  params.append('page_size', '5');
  params.append('page', '1');

  return axios
    .get<TransactionsCountResponse>(
      `/companies/${companyId}/products/banking/accounts/${accountId}/banking_info/transactions/?${params.toString()}`
    )
    .then(({ data: { results } }) =>
      results.map(({ createdAt, amount, currency, transactionType }) => {
        return {
          date: createdAt,
          account: currency,
          amount,
          type: transactionType,
          currency,
        };
      })
    );
}

export function getConfirmationLetter(
  companyId: number,
  accountId: number,
  transactionId: number,
  withFeeInfo: boolean = true
): Promise<any> {
  const params = new URLSearchParams();
  params.append('with_fee_info', withFeeInfo.toString());
  return axios
    .get(
      `/companies/${companyId}/products/banking/accounts/${accountId}/banking_info/transactions/${transactionId}/confirmation_letter/?${params.toString()}`,
      { responseType: 'blob' }
    )
    .then((data) => data);
}

export function getTransferDetails(
  companyId: number,
  accountId: number,
  transferSourceId: string
): Promise<TransactionDetail> {
  return axios
    .get<PaymentDetail>(
      `/companies/${companyId}/products/banking/accounts/${accountId}/banking_info/payments/${transferSourceId}/`
    )
    .then(({ data }) => data);
}

export function getTransactionDetails(
  companyId: number,
  accountId: number,
  transactionId: number,
  transactionType: TransactionTypeEnum
): Promise<TransactionDetail> {
  switch (transactionType) {
    case TransactionTypeEnum.DEPOSIT:
      return axios
        .get<DepositDetail>(
          `/companies/${companyId}/products/banking/accounts/${accountId}/banking_info/deposits/${transactionId}/`
        )
        .then(({ data }) => data);
    case TransactionTypeEnum.PAYMENT:
      return axios
        .get<PaymentDetail>(
          `/companies/${companyId}/products/banking/accounts/${accountId}/banking_info/payments/${transactionId}/`
        )
        .then(({ data }) => data);
    case TransactionTypeEnum.CONVERSION:
      return axios
        .get<ConversionDetail>(
          `/companies/${companyId}/products/banking/accounts/${accountId}/banking_info/conversions/${transactionId}/`
        )
        .then(({ data }) => data);
    case TransactionTypeEnum.CARD_TRANSACTION:
      return axios
        .get<CardPurchaseDetail>(
          `/companies/${companyId}/products/banking/accounts/${accountId}/banking_info/card_transactions/${transactionId}/`
        )
        .then(({ data }) => data);
    case TransactionTypeEnum.CHARGE:
      return axios
        .get<ChargeDetail>(
          `/companies/${companyId}/products/banking/accounts/${accountId}/banking_info/charges/${transactionId}/`
        )
        .then(({ data }) => data);
    default:
      return Promise.resolve({} as PaymentDetail);
  }
}

export function adminCreateGlobalAccount(accountId: number, payload: any) {
  return axios
    .post<GlobalAccount>(`/admins/products/banking/accounts/${accountId}/global_accounts/`, payload)
    .then(({ data }) => data);
}

export function adminGetConfirmationLetter(accountId: number, transactionId: number, withFeeInfo: boolean) {
  const params = new URLSearchParams();
  params.append('with_fee_info', withFeeInfo.toString());
  return axios
    .get(
      `/admins/products/banking/accounts/${accountId}/transactions/${transactionId}/confirmation_letter/?${params.toString()}`,
      { responseType: 'blob' }
    )
    .then((data) => data);
}

export function markComplianceNotified(companyId: number, productStatusId: number) {
  return axios
    .post(`/companies/${companyId}/products/product_status/${productStatusId}/mark_compliance_notified/`)
    .then(({ data }) => data);
}

export function complianceInvite(companyId: number, productStatusId: number) {
  return axios
    .post(`/companies/${companyId}/products/product_status/${productStatusId}/compliance_invite/`)
    .then(({ data }) => data);
}

export function startBanking(companyId: number, productStatusId: number) {
  return axios
    .patch(`/companies/${companyId}/products/product_status/${productStatusId}/`, {
      hasStartedBanking: true,
    })
    .then(({ data }) => data);
}

export function getRecipients(companyId: number, accountId: number, page: number = 1, type?: string) {
  // TODO: page parameter is not used in the backend?
  return axios
    .get<{
      beneficiaries: Recipient[];
      hasMore: boolean;
    }>(`/companies/${companyId}/products/banking/accounts/${accountId}/banking_info/beneficiaries/`, {
      params: type ? { type } : undefined,
    })
    .then(({ data: { beneficiaries, ...rest } }) => ({ recipients: beneficiaries, ...rest }));
}

export function getRecipient(companyId: number, accountId: number, recipientId: string) {
  return axios
    .get<
      Nullable<Recipient>
    >(`/companies/${companyId}/products/banking/accounts/${accountId}/banking_info/beneficiaries/${recipientId}/`)
    .then(({ data }) => data);
}

export function createRecipient(companyId: number, accountId: number, payload: any) {
  return axios
    .post(
      `/companies/${companyId}/products/banking/accounts/${accountId}/banking_info/beneficiaries/`,
      payload
    )
    .then(({ data }) => data);
}

export function updateRecipient(companyId: number, accountId: number, recipientId: string, payload: any) {
  return axios
    .patch(
      `/companies/${companyId}/products/banking/accounts/${accountId}/banking_info/beneficiaries/${recipientId}/`,
      payload
    )
    .then(({ data }) => data);
}

export function getTransfers(companyId: number, accountId: number, filters: any, pageSize = 10) {
  const params = new URLSearchParams();
  params.append('page_size', `${pageSize}`);
  filters &&
    Object.keys(filters).forEach((key) => {
      params.append(key, filters[key]);
    });
  return axios
    .get<PaymentsCountResponse>(
      `/companies/${companyId}/products/banking/accounts/${accountId}/banking_info/payments/?${params.toString()}`
    )
    .then(({ data }) => data);
}

export function getTransferDetail(companyId: number, accountId: number, transferId: number) {
  return axios
    .get<PaymentDetail>(
      `/companies/${companyId}/products/banking/accounts/${accountId}/banking_info/payments/${transferId}/`
    )
    .then(({ data }) => data);
}

// Field type definitions
type FieldType = 'dateRange' | 'array' | 'string';

const FIELD_TYPES: Record<keyof ReportingState, FieldType> = {
  dateRange: 'dateRange',
  transactionTypes: 'array',
  currencies: 'array',
  fileFormat: 'string',
  timezone: 'string',
  filename: 'string',
  currency: 'string', //For cash control account details
};

function processReportField(params: URLSearchParams, key: string, value: any) {
  if (!value) return;

  const snakeKey = camelToSnake(key);
  const fieldType = FIELD_TYPES[key as keyof ReportingState];

  switch (fieldType) {
    case 'dateRange':
      params.append('from_created_at', dayjs(value.fromDate).format('YYYY-MM-DD'));
      params.append('to_created_at', dayjs(value.toDate).format('YYYY-MM-DD'));
      break;
    case 'array':
      if (Array.isArray(value)) {
        value.forEach((item) => params.append(snakeKey, item.toString()));
      }
      break;
    default:
      params.append(snakeKey, value.toString());
  }
}

export function getBankingReport(
  companyId: number,
  accountId: number,
  reportName: string,
  reportFields: ReportingState
) {
  const params = new URLSearchParams();
  Object.entries(reportFields).forEach(([key, value]) => processReportField(params, key, value));

  return axios
    .get(
      `/companies/${companyId}/products/banking/accounts/${accountId}/banking_info/financial_reports/${reportName}/?${params.toString()}`,
      { responseType: 'blob' }
    )
    .then(({ data, status }) => ({ data, status }));
}

export function adminGetBankingReport(accountId: number, reportName: string, reportFields: ReportingState) {
  const params = new URLSearchParams();
  Object.entries(reportFields).forEach(([key, value]) => processReportField(params, key, value));

  return axios
    .get(
      `/admins/products/banking/accounts/${accountId}/financial_reports/${reportName}/?${params.toString()}`,
      { responseType: 'blob' }
    )
    .then(({ data, status }) => ({ data, status }));
}

export function adminExportConnectedAccounts(filters: any, format: 'csv' | 'xlsx'): Promise<Blob> {
  const params = new URLSearchParams();
  params.append('file_format', format);
  filters &&
    Object.keys(filters).forEach((key) => {
      params.append(key, filters[key]);
    });
  return axios
    .get(`admins/products/banking/accounts/export/?${params.toString()}`, {
      responseType: 'blob',
    })
    .then(({ data }) => data);
}

type LowActivityAccountsResponse = {
  results: LowActivityAccount[];
  count: number;
};

export function adminGetLowActivityAccounts(page: number, pageSize: number, search?: string) {
  const params = new URLSearchParams();
  params.append('page', `${page}`);
  params.append('page_size', `${pageSize}`);
  if (search) params.append('search', search);
  return axios
    .get<LowActivityAccountsResponse>(`admins/products/banking/accounts/low_activity/?${params.toString()}`)
    .then(({ data }) => data);
}

export function adminExportLowActivityAccounts(search?: string): Promise<Blob> {
  const params = new URLSearchParams();
  if (search) params.append('search', search);
  return axios
    .get(`admins/products/banking/accounts/low_activity/export/?${params.toString()}`, {
      responseType: 'blob',
    })
    .then(({ data }) => data);
}

export function adminImportLowActivityAccounts(
  payload: FormData | { accountIds: string[]; append: boolean }
) {
  return axios
    .post<LowActivityImportResult>(`admins/products/banking/accounts/low_activity/import/`, payload)
    .then(({ data }) => data);
}

export function adminSetAccountLowActivity(accountId: number, hasLowActivity: boolean) {
  return axios
    .patch<LowActivityStatus>(`admins/products/banking/accounts/${accountId}/low_activity/`, {
      hasLowActivity,
    })
    .then(({ data }) => data);
}

export function adminBlockAccountDebits(accountId: number) {
  return axios
    .post<DebitsBlockStatus>(`admins/products/banking/accounts/${accountId}/debits/block/`)
    .then(({ data }) => data);
}

export function adminUnblockAccountDebits(accountId: number) {
  return axios
    .post<DebitsBlockStatus>(`admins/products/banking/accounts/${accountId}/debits/unblock/`)
    .then(({ data }) => data);
}

export function getEmbeddedAuthData(companyId: number, accountId: number, scope: string | string[]) {
  const scopeParam = Array.isArray(scope) ? scope.join(',') : scope;
  return axios
    .get(
      `/companies/${companyId}/products/banking/accounts/${accountId}/banking_info/auth/embedded_component/?scope=${scopeParam}`
    )
    .then(({ data }) => data);
}

export function getPendingRfis(companyId: number, accountId: number): Promise<PendingRfi[]> {
  return axios
    .get(`/companies/${companyId}/products/banking/accounts/${accountId}/rfis/`)
    .then(({ data }) => data);
}

interface GetRfisParams {
  page?: number;
  pageSize?: number;
  status?: string[];
  rfiType?: string[];
}

export function getRfis(
  companyId: number,
  accountId: number,
  { page, pageSize, status, rfiType }: GetRfisParams = {}
): Promise<RfisCountResponse> {
  const params = new URLSearchParams();
  if (page) params.append('page', String(page));
  if (pageSize) params.append('page_size', String(pageSize));
  if (status?.length) params.append('status', status.join(','));
  if (rfiType?.length) params.append('rfi_type', rfiType.join(','));
  return axios
    .get<RfisCountResponse>(
      `/companies/${companyId}/products/banking/accounts/${accountId}/rfis/list/?${params.toString()}`
    )
    .then(({ data }) => data);
}

export function getRfiDetail(companyId: number, accountId: number, rfiPk: number): Promise<PendingRfi> {
  return axios
    .get(`/companies/${companyId}/products/banking/accounts/${accountId}/rfis/${rfiPk}/`)
    .then(({ data }) => data);
}

// export function createTransfer(companyId: number, payload: any): Promise<Payment> {
//   return axios
//     .post(`/companies/${companyId}/products/banking/banking_info/payments/`, payload)
//     .then(({ data }) => data)
//     .catch((error) => {
//       // Throw the API error response instead of the Axios error
//       throw error.response?.data || error;
//     });
// }

export function getBankingRate(
  companyId: number,
  accountId: number,
  buyCurrency: BankingCurrency,
  sellCurrency: BankingCurrency
): Promise<Rate> {
  const payload = {
    buyCurrency,
    sellCurrency,
    sellAmount: 1000, // This endpoint does not work with small numbers, so we set the sell amount to 1000
  };
  return axios
    .post<BackendRate>(
      `/companies/${companyId}/products/banking/accounts/${accountId}/banking_info/conversion_rate/`,
      payload
    )
    .then(({ data }) => ({
      ...data,
      // Divide by sellAmount to get the rate
      sellAmount: 1,
      buyAmount: data.buyAmount / data.sellAmount,
      // Get currency pair from the response
      strongCurrency: data.currencyPair ? data.currencyPair.substring(0, 3) : '', // first 3 characters
      weakCurrency: data.currencyPair ? data.currencyPair.substring(3) : '', // last 3 characters
    }));
}

export function createConversion(
  companyId: number,
  accountId: number,
  buyAmount: Nullable<number>,
  sellAmount: Nullable<number>,
  buyCurrency: BankingCurrency,
  sellCurrency: BankingCurrency,
  quoteId: string
): Promise<ConversionDetail> {
  const payload = {
    buyCurrency,
    sellCurrency,
    quoteId,
    ...(buyAmount !== null && { buyAmount }),
    ...(sellAmount !== null && { sellAmount }),
  };
  return axios
    .post<ConversionDetail>(
      `/companies/${companyId}/products/banking/accounts/${accountId}/banking_info/conversions/`,
      payload
    )
    .then(({ data }) => data);
}

export function getConversions(companyId: number, accountId: number, filters: any) {
  const params = new URLSearchParams();
  filters &&
    Object.keys(filters).forEach((key) => {
      params.append(key, filters[key]);
    });
  return axios
    .get<ConversionsCountResponse>(
      `/companies/${companyId}/products/banking/accounts/${accountId}/banking_info/conversions/?${params.toString()}`
    )
    .then(({ data }) => data);
}

export function getConversionDetail(companyId: number, accountId: number, conversionId: number) {
  return axios
    .get<ConversionDetail>(
      `/companies/${companyId}/products/banking/accounts/${accountId}/banking_info/conversions/${conversionId}/`
    )
    .then(({ data }) => data);
}

export function getCards(
  companyId: number,
  accountId: number,
  page: number = 0,
  pageSize: number = 10,
  cardType?: 'COMPANY' | 'EMPLOYEE'
) {
  const params = new URLSearchParams();
  params.append('page', `${page.toString()}`);
  params.append('page_size', `${pageSize.toString()}`);
  if (cardType) {
    params.append('card_type', cardType);
  }
  return axios
    .get<
      PaginatedResponse<Card>
    >(`/companies/${companyId}/products/banking/accounts/${accountId}/banking_info/cards/?${params.toString()}`)
    .then(({ data }) => data);
}

export function getCardDetails(companyId: number, accountId: number, cardId: number) {
  return axios
    .get<CardDetails>(
      `/companies/${companyId}/products/banking/accounts/${accountId}/banking_info/cards/${cardId}/`
    )
    .then(({ data }) => data);
}

export function getAllAccountsCards(
  pageSize: number = 10,
  filters: {
    page: number;
    cardType?: CardFormFactorType[];
    cardStatus?: CardStatus[];
    search?: string;
  }
) {
  const queryParams = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => {
    const snakeKey = camelToSnake(key);
    if (Array.isArray(value)) {
      value.forEach((item: string) => queryParams.append(snakeKey, item));
    } else if (value !== undefined && value !== null && value !== '') {
      queryParams.append(snakeKey, value.toString());
    }
  });
  queryParams.append('page_size', pageSize.toString());
  return axios
    .get<PaginatedResponse<Card>>(`/admins/products/banking/cards/?${queryParams.toString()}`)
    .then(({ data }) => {
      return data;
    });
}

export function adminGetCardDetail(accountId: number, cardId: number) {
  return axios
    .get<CardDetails>(`/admins/products/banking/accounts/${accountId}/cards/${cardId}/`)
    .then(({ data }) => data);
}

export function createCard(companyId: number, accountId: number, cardData: CreateCardPayload) {
  return axios
    .post<AirwallexCardDetails>(
      `/companies/${companyId}/products/banking/accounts/${accountId}/banking_info/cards/`,
      cardData
    )
    .then(({ data }) => data);
}

export function updateCard(
  companyId: number,
  accountId: number,
  cardId: number,
  cardData: UpdateCardPayload
) {
  return axios
    .patch<AirwallexCardDetails>(
      `/companies/${companyId}/products/banking/accounts/${accountId}/banking_info/cards/${cardId}/`,
      cardData
    )
    .then(({ data }) => data);
}

export function createCardholder(companyId: number, accountId: number, payload: CreateCardholderPayload) {
  return axios
    .post<Cardholder>(
      `/companies/${companyId}/products/banking/accounts/${accountId}/banking_info/cardholders/`,
      payload
    )
    .then(({ data }) => data);
}

export function getCardholderByEmail(companyId: number, accountId: number, email: string) {
  return axios
    .get<Cardholder>(
      `/companies/${companyId}/products/banking/accounts/${accountId}/banking_info/cardholder-search/?email=${encodeURIComponent(
        email
      )}`
    )
    .then(({ data }) => (Object.keys(data).length === 0 ? null : data));
}

export function adminGetCards(accountId: number, page: number, pageSize: number = 10) {
  const params = new URLSearchParams();
  params.append('page', `${page.toString()}`);
  params.append('page_size', `${pageSize.toString()}`);
  return axios
    .get<
      PaginatedResponse<Card>
    >(`/admins/products/banking/accounts/${accountId}/cards/?${params.toString()}`)
    .then(({ data }) => data);
}

export function deleteRecipient(
  companyId: number,
  accountId: number,
  recipientId: string
): Promise<DeleteRecipientResponse> {
  return axios
    .delete(
      `/companies/${companyId}/products/banking/accounts/${accountId}/banking_info/beneficiaries/${recipientId}/`
    )
    .then(({ data }) => data);
}

export function getGlobalAccounts(
  companyId: number,
  accountId: number,
  page: number = 1,
  pageSize: number = 10
) {
  const params = new URLSearchParams();
  params.append('page_num', `${page.toString()}`);
  params.append('page_size', `${pageSize.toString()}`);
  return axios
    .get<GlobalAccountsResponse>(
      `/companies/${companyId}/products/banking/accounts/${accountId}/banking_info/global_accounts/?${params.toString()}`
    )
    .then(({ data }) => data);
}

export function getGlobalAccountCreationOptions(companyId: number, accountId: number) {
  return axios
    .get<
      GlobalAccountCreationOption[]
    >(`/companies/${companyId}/products/banking/accounts/${accountId}/banking_info/global_accounts/creation_options/`)
    .then(({ data }) => data);
}

export function getGlobalAccountDetail(companyId: number, accountId: number, id: number) {
  return axios
    .get<GlobalAccount>(
      `/companies/${companyId}/products/banking/accounts/${accountId}/banking_info/global_accounts/${id}/`
    )
    .then(({ data }) => data);
}

export function exportGlobalAccountStatement(companyId: number, accountId: number, globalAccountId: string) {
  return axios
    .get(
      `/companies/${companyId}/products/banking/accounts/${accountId}/banking_info/global_accounts/${globalAccountId}/export_statement/`,
      {
        responseType: 'blob',
      }
    )
    .then((data) => data);
}

export function createGlobalAccount(
  companyId: number,
  accountId: number,
  payload: CreateGlobalAccountRequest
) {
  return axios
    .post<CreateGlobalAccountResponse>(
      `/companies/${companyId}/products/banking/accounts/${accountId}/banking_info/global_accounts/`,
      payload
    )
    .then(({ data }) => data);
}

export function updateGlobalAccountSettings(companyId: number, accountId: number, payload: any) {
  return axios
    .post<ConnectedAccount>(
      `/companies/${companyId}/products/banking/accounts/${accountId}/banking_info/global_accounts/settings/`,
      payload
    )
    .then(({ data }) => data);
}

export function getCardPanToken(companyId: number, accountId: number, cardId: number) {
  return axios
    .get<{
      token: string;
      expiresAt: string;
    }>(
      `/companies/${companyId}/products/banking/accounts/${accountId}/banking_info/cards/${cardId}/get_pan_token/`
    )
    .then(({ data }) => data);
}

export function getTransferFees(
  companyId: number,
  accountId: number,
  filters: TransferFeesFilters
): Promise<TransferFeesResponse> {
  const params = new URLSearchParams();
  filters &&
    Object.keys(filters).forEach((key) => {
      const value = filters[key as keyof TransferFeesFilters];
      if (value) {
        params.append(decamelize(key), value);
      }
    });
  return axios
    .get<TransferFeesResponse>(
      `/companies/${companyId}/products/banking/accounts/${accountId}/banking_info/payments/fees/?${params.toString()}`
    )
    .then(({ data }) => data);
}

export function createTransfer(
  companyId: number,
  accountId: number,
  payload: CreateTransferPayload
): Promise<PaymentDetail> {
  return axios
    .post<PaymentDetail>(
      `/companies/${companyId}/products/banking/accounts/${accountId}/banking_info/payments/`,
      payload
    )
    .then(({ data }) => data);
}

// =======================
// Lender transfer flow (cash control lenders without a preloaded beneficiary).
// Lender-exclusive endpoints scoped by the cash control account only.
// =======================

export function getLenderRecipients(accountId: number) {
  return axios
    .get<{ beneficiaries: Recipient[]; hasMore: boolean }>(`/lender/clients/${accountId}/beneficiaries/`)
    .then(({ data: { beneficiaries, ...rest } }) => ({ recipients: beneficiaries, ...rest }));
}

export function getLenderRecipient(accountId: number, recipientId: string) {
  return axios
    .get<Nullable<Recipient>>(`/lender/clients/${accountId}/beneficiaries/${recipientId}/`)
    .then(({ data }) => data);
}

export function createLenderRecipient(accountId: number, payload: any) {
  return axios.post(`/lender/clients/${accountId}/beneficiaries/`, payload).then(({ data }) => data);
}

export function getLenderBankingDetails(accountId: number): Promise<BankingDetails> {
  return axios.get<BankingDetails>(`/lender/clients/${accountId}/banking_details/`).then(({ data }) => data);
}

export function getLenderEmbeddedAuthData(accountId: number, scope: string) {
  return axios
    .get(`/lender/clients/${accountId}/auth/embedded_component/?scope=${scope}`)
    .then(({ data }) => data);
}

export function getLenderTransferFees(
  accountId: number,
  filters: TransferFeesFilters
): Promise<TransferFeesResponse> {
  const params = new URLSearchParams();
  filters &&
    Object.keys(filters).forEach((key) => {
      const value = filters[key as keyof TransferFeesFilters];
      if (value) {
        params.append(decamelize(key), value);
      }
    });
  return axios
    .get<TransferFeesResponse>(`/lender/clients/${accountId}/payments/fees/?${params.toString()}`)
    .then(({ data }) => data);
}

export function createLenderTransfer(
  accountId: number,
  payload: CreateTransferPayload
): Promise<PaymentDetail> {
  return axios
    .post<PaymentDetail>(`/lender/clients/${accountId}/payments/`, payload)
    .then(({ data }) => data);
}

// =======================
// Batch Transfers (Fees/Quotes/Submit)
// =======================

export async function getBatchTransferFees(
  companyId: number,
  accountId: number,
  items: BatchFeesRequestItem[]
): Promise<BatchFeesResponse> {
  return axios
    .post<BatchFeesResponse>(
      `/companies/${companyId}/products/banking/accounts/${accountId}/banking_info/batch_transfers/fees/`,
      items
    )
    .then(({ data }) => data);
}

export async function getBatchQuote(
  companyId: number,
  accountId: number,
  batchId: number
): Promise<BatchQuoteResponse> {
  return axios
    .post<BatchQuoteResponse>(
      `/companies/${companyId}/products/banking/accounts/${accountId}/banking_info/batch_transfers/${batchId}/quote/`
    )
    .then(({ data }) => data);
}

export async function createBatchTransfer(
  companyId: number,
  accountId: number,
  batchId: number
): Promise<BatchSubmitResponse> {
  return axios
    .post<BatchSubmitResponse>(
      `/companies/${companyId}/products/banking/accounts/${accountId}/banking_info/batch_transfers/${batchId}/`
    )
    .then(({ data }) => data);
}

export async function getBatchTransfers(
  companyId: number,
  accountId: number,
  filters: BankingPageBatchTransfersFilters
) {
  const params = new URLSearchParams();
  params.append('page', filters.page.toString());
  params.append('page_size', filters.pageSize.toString());
  return axios
    .get<BatchTransfersResponse>(
      `/companies/${companyId}/products/banking/accounts/${accountId}/banking_info/batch_transfers/?${params.toString()}`
    )
    .then(({ data }) => data);
}

export async function getBatchTransferDetail(companyId: number, accountId: number, batchId: number) {
  return axios
    .get<BatchTransferDetail>(
      `/companies/${companyId}/products/banking/accounts/${accountId}/banking_info/batch_transfers/${batchId}/`
    )
    .then(({ data }) => data);
}

export function getTransferConfigs(companyId: number) {
  return axios
    .get<TransferConfig[]>(`/companies/${companyId}/products/banking/banking_info/transfer_configs/`)
    .then(({ data }) => data);
}

export function createTransferConfig(
  companyId: number,
  payload: CreateTransferConfigPayload
): Promise<TransferConfig> {
  return axios
    .post<TransferConfig>(`/companies/${companyId}/products/banking/banking_info/transfer_configs/`, payload)
    .then(({ data }) => data);
}

export function updateTransferConfig(
  companyId: number,
  transferConfigId: number,
  payload: UpdateTransferConfigPayload
): Promise<TransferConfig> {
  return axios
    .patch<TransferConfig>(
      `/companies/${companyId}/products/banking/banking_info/transfer_configs/${transferConfigId}/`,
      payload
    )
    .then(({ data }) => data);
}

export function deleteTransferConfig(companyId: number, transferConfigId: number): Promise<void> {
  return axios.delete(
    `/companies/${companyId}/products/banking/banking_info/transfer_configs/${transferConfigId}/`
  );
}

export function getTransferRequests(
  companyId: number,
  accountId: number,
  filters: BankingPageTransferRequestsFilters
) {
  const params = new URLSearchParams();
  filters &&
    Object.keys(filters).forEach((key) => {
      params.append(key, filters[key as keyof BankingPageTransferRequestsFilters].toString());
    });
  return axios
    .get<
      PaginatedResponse<TransferRequest>
    >(`/companies/${companyId}/products/banking/accounts/${accountId}/banking_info/transfer_requests/?${params.toString()}`)
    .then(({ data }) => data);
}

export function getTransferRequest(companyId: number, accountId: number, transferRequestId: number) {
  return axios
    .get<TransferRequest>(
      `/companies/${companyId}/products/banking/accounts/${accountId}/banking_info/transfer_requests/${transferRequestId}/`
    )
    .then(({ data }) => data);
}

export function submitTransferRequest(
  companyId: number,
  accountId: number,
  transferRequestId: number,
  payload: SubmitPaymentFromTransferRequestPayload
): Promise<PaymentDetail> {
  return axios
    .post<PaymentDetail>(
      `/companies/${companyId}/products/banking/accounts/${accountId}/banking_info/transfer_requests/${transferRequestId}/`,
      payload
    )
    .then(({ data }) => data);
}

export function cancelTransferRequest(
  companyId: number,
  accountId: number,
  transferRequestId: number
): Promise<TransferRequest> {
  return axios
    .delete<TransferRequest>(
      `/companies/${companyId}/products/banking/accounts/${accountId}/banking_info/transfer_requests/${transferRequestId}/`
    )
    .then(({ data }) => data);
}

export function cancelScheduledTransfer(
  companyId: number,
  accountId: number,
  paymentId: number
): Promise<void> {
  return axios
    .post(
      `/companies/${companyId}/products/banking/accounts/${accountId}/banking_info/payments/${paymentId}/cancel/`
    )
    .then(({ data }) => data);
}

export function shareTransferReceipt(
  companyId: number,
  accountId: number,
  paymentId: number,
  email: string,
  files?: File[]
): Promise<void> {
  const formData = new FormData();
  formData.append('email', email);

  if (files && files.length > 0) {
    files.forEach((file) => {
      formData.append('assets', file);
    });
  }

  return axios
    .post(
      `/companies/${companyId}/products/banking/accounts/${accountId}/banking_info/payments/${paymentId}/receipt/`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    )
    .then(({ data }) => data);
}

// =======================
// Recurring Payments
// =======================

const recurringPaymentsBase = (companyId: number, accountId: number) =>
  `/companies/${companyId}/products/banking/accounts/${accountId}/banking_info/recurring_payments`;

export function getRecurringPayments(
  companyId: number,
  accountId: number
): Promise<RecurringPaymentsResponse> {
  return axios
    .get<RecurringPaymentsResponse>(`${recurringPaymentsBase(companyId, accountId)}/`)
    .then(({ data }) => data);
}

export function createRecurringPayment(
  companyId: number,
  accountId: number,
  payload: CreateRecurringPaymentPayload
): Promise<RecurringPayment> {
  return axios
    .post<RecurringPayment>(`${recurringPaymentsBase(companyId, accountId)}/`, payload)
    .then(({ data }) => data);
}

export function updateRecurringPayment(
  companyId: number,
  accountId: number,
  recurringPaymentId: number,
  payload: UpdateRecurringPaymentPayload
): Promise<RecurringPayment> {
  return axios
    .patch<RecurringPayment>(`${recurringPaymentsBase(companyId, accountId)}/${recurringPaymentId}/`, payload)
    .then(({ data }) => data);
}

export function deleteRecurringPayment(
  companyId: number,
  accountId: number,
  recurringPaymentId: number
): Promise<void> {
  return axios
    .delete(`${recurringPaymentsBase(companyId, accountId)}/${recurringPaymentId}/`)
    .then(({ data }) => data);
}

export function getRecurringPaymentExecutions(
  companyId: number,
  accountId: number,
  recurringPaymentId: number
): Promise<{ count: number; results: RecurringPaymentExecution[] }> {
  return axios
    .get(`${recurringPaymentsBase(companyId, accountId)}/${recurringPaymentId}/executions/`)
    .then(({ data }) => data);
}

export function retryRecurringPayment(
  companyId: number,
  accountId: number,
  recurringPaymentId: number
): Promise<PaymentDetail> {
  return axios
    .post<PaymentDetail>(`${recurringPaymentsBase(companyId, accountId)}/${recurringPaymentId}/retry/`)
    .then(({ data }) => data);
}
